(() => {
  const initialize = async (section) => {
    if (section.dataset.carouselInitialized) return;
    section.dataset.carouselInitialized = 'true';
    const navigation = section.querySelector('[data-review-navigation]');
    const previous = section.querySelector('[data-review-previous]');
    const next = section.querySelector('[data-review-next]');
    const widget = section.querySelector('.jdgm-review-widget');
    const controller = new AbortController();
    const track = document.createElement('div');
    track.className = 'luvire-book-reviews__track';
    track.tabIndex = 0;
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', navigation.getAttribute('aria-label'));
    const pause = section.querySelector('[data-review-pause]');
    let stops = [0];
    let frame;
    let autoFrame;
    let autoTimer;
    let drag;
    let hovered = false;
    let touchHeld = false;
    let visible = false;
    let paused = false;
    let autoDirection = 1;
    let suppressClickUntil = 0;
    let lastInteraction = performance.now();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const activeIndex = () => stops.reduce((best, stop, index) => Math.abs(stop - track.scrollLeft) < Math.abs(stops[best] - track.scrollLeft) ? index : best, 0);
    const stopAuto = () => {
      cancelAnimationFrame(autoFrame);
      autoFrame = undefined;
      track.classList.remove('is-auto-scrolling');
    };
    const canAuto = () => pause && visible && !document.hidden && !paused && !hovered && !touchHeld && !drag && !reducedMotion.matches && stops.length > 1 && !section.querySelector(':focus-visible, .luvire-book-reviews__more[aria-expanded="true"]');
    const animateAuto = target => {
      const origin = track.scrollLeft;
      const started = performance.now();
      track.classList.add('is-auto-scrolling');
      const step = now => {
        const progress = Math.min(1, (now - started) / 1100);
        track.scrollLeft = origin + (target - origin) * (1 - Math.cos(Math.PI * progress)) / 2;
        if (progress < 1) { autoFrame = requestAnimationFrame(step); return; }
        track.scrollLeft = target;
        autoFrame = undefined;
        track.classList.remove('is-auto-scrolling');
        lastInteraction = performance.now();
        scheduleAuto();
      };
      autoFrame = requestAnimationFrame(step);
    };
    function scheduleAuto() {
      clearTimeout(autoTimer);
      if (!canAuto() || autoFrame !== undefined) return;
      autoTimer = setTimeout(() => {
        if (!canAuto()) return;
        const index = activeIndex();
        if (index >= stops.length - 1) autoDirection = -1;
        if (index === 0) autoDirection = 1;
        animateAuto(stops[index + autoDirection]);
      }, Math.max(0, 5000 - (performance.now() - lastInteraction)));
    }
    const renewIdle = () => {
      lastInteraction = performance.now();
      stopAuto();
      scheduleAuto();
    };
    const update = () => {
      const active = activeIndex();
      previous.disabled = active === 0;
      next.disabled = active === stops.length - 1;
    };
    const measure = () => {
      stopAuto();
      const cards = [...track.children];
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const origin = cards[0]?.getBoundingClientRect().left || 0;
      stops = [0];
      cards.slice(1).forEach(card => {
        const stop = Math.min(max, Math.max(0, card.getBoundingClientRect().left - origin));
        if (stop - stops[stops.length - 1] > 2) stops.push(stop);
      });
      cards.forEach(card => {
        const body = card.querySelector('.luvire-book-reviews__body');
        const more = card.querySelector('.luvire-book-reviews__more');
        if (body.classList.contains('is-collapsed')) more.hidden = body.scrollHeight <= body.clientHeight + 1;
      });
      navigation.hidden = stops.length < 2;
      if (pause) pause.hidden = reducedMotion.matches;
      update();
      scheduleAuto();
    };
    const move = direction => {
      stopAuto();
      track.scrollTo({left: stops[Math.max(0, Math.min(stops.length - 1, activeIndex() + direction))], behavior: reducedMotion.matches ? 'instant' : 'smooth'});
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    pause?.addEventListener('click', () => {
      paused = !paused;
      pause.setAttribute('aria-pressed', String(paused));
      pause.setAttribute('aria-label', paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
      pause.querySelector('path').setAttribute('d', paused ? 'm12 5 12 7-12 7Z' : 'M11 5v14M21 5v14');
    });
    section.addEventListener('click', renewIdle, {signal: controller.signal});
    section.addEventListener('pointerdown', renewIdle, {passive: true, signal: controller.signal});
    section.addEventListener('wheel', renewIdle, {passive: true, signal: controller.signal});
    section.addEventListener('keydown', renewIdle, {signal: controller.signal});
    section.addEventListener('focusin', renewIdle, {signal: controller.signal});
    section.addEventListener('focusout', () => queueMicrotask(renewIdle), {signal: controller.signal});
    document.addEventListener('visibilitychange', renewIdle, {signal: controller.signal});
    reducedMotion.addEventListener('change', measure, {signal: controller.signal});
    track.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; renewIdle(); } });
    track.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { hovered = false; renewIdle(); } });
    track.addEventListener('scroll', () => {
      if (autoFrame === undefined && !track.classList.contains('is-auto-scrolling')) renewIdle();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    }, {passive: true});
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    });
    track.addEventListener('dragstart', event => event.preventDefault());
    track.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch') { touchHeld = true; renewIdle(); return; }
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, a, input, select, textarea, summary')) return;
      event.preventDefault();
      track.classList.add('is-dragging');
      stopAuto();
      drag = {id: event.pointerId, x: event.clientX, left: track.scrollLeft, index: activeIndex(), moved: false};
      track.setPointerCapture(event.pointerId);
    });
    track.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const distance = event.clientX - drag.x;
      if (Math.abs(distance) < 6 && !drag.moved) return;
      drag.moved = true;
      event.preventDefault();
      track.scrollLeft = drag.left - distance;
    });
    const finishDrag = event => {
      if (event.pointerType === 'touch') { touchHeld = false; renewIdle(); return; }
      if (!drag || drag.id !== event.pointerId) return;
      const current = drag;
      const distance = track.scrollLeft - current.left;
      let index = activeIndex();
      if (current.moved && index === current.index && Math.abs(distance) > 35) index = Math.max(0, Math.min(stops.length - 1, index + Math.sign(distance)));
      drag = undefined;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      if (current.moved) {
        suppressClickUntil = performance.now() + 350;
        track.scrollTo({left: stops[index], behavior: reducedMotion.matches ? 'instant' : 'smooth'});
      }
      renewIdle();
    };
    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('lostpointercapture', finishDrag);
    track.addEventListener('click', event => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    const resize = new ResizeObserver(measure);
    const viewport = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .2);
      renewIdle();
    }, {threshold: [0, .2]});
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(section)) return;
      controller.abort();
      observer.disconnect();
      resize.disconnect();
      viewport.disconnect();
      clearTimeout(autoTimer);
      stopAuto();
      cancelAnimationFrame(frame);
    }, {signal: controller.signal});
    const enhanceRating = () => {
      const rating = widget.querySelector('.jm-average-rating-display');
      const value = Number.parseFloat(rating?.querySelector('.jm-text')?.textContent || '');
      if (!rating || !Number.isFinite(value)) return;
      let stars = rating.querySelector('.luvire-book-reviews__rating-stars');
      if (!stars) {
        stars = document.createElement('span');
        stars.className = 'luvire-book-reviews__rating-stars';
        stars.setAttribute('aria-hidden', 'true');
        stars.textContent = '★★★★★';
        rating.prepend(stars);
      }
      stars.style.setProperty('--rating-fill', `${Math.max(0, Math.min(5, value)) * 20}%`);
    };
    const observer = new MutationObserver(enhanceRating);
    observer.observe(widget, {childList: true, subtree: true});
    enhanceRating();
    const text = (tag, className, value) => {
      const element = document.createElement(tag);
      element.className = className;
      element.textContent = value;
      return element;
    };
    const cardFrom = source => {
      const card = document.createElement('article');
      card.className = 'jm-review-item luvire-book-reviews__card';
      card.dataset.reviewId = source.dataset.reviewId;
      const author = source.querySelector('.jdgm-rev__author')?.textContent.trim() || '';
      const score = Number(source.querySelector('.jdgm-rev__rating')?.dataset.score) || 0;
      const stars = text('span', 'luvire-book-reviews__card-stars', '★'.repeat(score) + '☆'.repeat(5 - score));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', (section.dataset.reviewStarsLabel || '__score__ out of 5 stars').replace('__score__', score));
      card.append(stars, text('h3', 'luvire-book-reviews__author', author));
      if (source.dataset.verifiedBuyer === 'true') card.append(text('span', 'luvire-book-reviews__verified', section.dataset.reviewVerified || 'Verified buyer'));
      const title = source.querySelector('.jdgm-rev__title')?.textContent.trim();
      if (title) card.append(text('h4', 'luvire-book-reviews__title', title));
      const sourceBody = source.querySelector('.jdgm-rev__body');
      sourceBody?.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
      sourceBody?.querySelectorAll('p').forEach(p => p.append('\n\n'));
      const bodyText = sourceBody?.textContent.trim().replace(/\n{3,}/g, '\n\n') || '';
      const body = text('p', 'luvire-book-reviews__body', bodyText);
      card.append(body);
      {
        body.classList.add('is-collapsed');
        const more = text('button', 'luvire-book-reviews__more', section.dataset.reviewReadMore || 'Read more');
        more.type = 'button';
        more.hidden = true;
        more.setAttribute('aria-expanded', 'false');
        more.addEventListener('click', () => {
          const collapsed = body.classList.toggle('is-collapsed');
          more.textContent = collapsed ? (section.dataset.reviewReadMore || 'Read more') : (section.dataset.reviewShowLess || 'Show less');
          more.setAttribute('aria-expanded', String(!collapsed));
        });
        card.append(more);
      }
      source.querySelectorAll('.jdgm-rev__pic-img').forEach(sourceImage => {
        const url = sourceImage.getAttribute('data-src') || sourceImage.getAttribute('src');
        if (!url || new URL(url, location.href).hostname !== 'review-images.judgeme.com') return;
        const image = document.createElement('img');
        image.src = url;
        image.alt = sourceImage.alt || (section.dataset.reviewPhotoLabel || '__author__ review photo').replace('__author__', author);
        image.width = 88;
        image.height = 88;
        image.loading = 'lazy';
        image.draggable = false;
        card.append(image);
      });
      return card;
    };
    try {
      // Public Widget API returns only storefront-safe, published review HTML.
      // Fetch separately from the mounted widget: carousel navigation never invokes its paginator.
      const fragment = document.createDocumentFragment();
      const seen = new Set();
      for (let page = 1; page <= 10; page += 1) {
        const url = new URL('https://api.judge.me/api/v1/widgets/product_review');
        url.search = new URLSearchParams({shop_domain: section.dataset.reviewShop, api_token: section.dataset.reviewPublicToken, external_id: widget.dataset.productId, per_page: '30', page: String(page)});
        const response = await fetch(url, {signal: controller.signal, credentials: 'omit'});
        if (!response.ok) throw new Error(`Review widget HTTP ${response.status}`);
        const payload = await response.json();
        const html = new DOMParser().parseFromString(payload.widget || '', 'text/html');
        const reviews = [...html.querySelectorAll('.jdgm-rev[data-review-id]')];
        let added = 0;
        reviews.forEach(review => {
          if (seen.has(review.dataset.reviewId)) return;
          seen.add(review.dataset.reviewId);
          fragment.append(cardFrom(review));
          added += 1;
        });
        if (!added || reviews.length < 30) break;
      }
      if (!seen.size) throw new Error('No published review cards');
      track.append(fragment);
      navigation.before(track);
      section.dataset.reviewContinuous = 'true';
      resize.observe(track);
      viewport.observe(track);
      measure();
    } catch (error) {
      // Leave the original Judge.me widget and its own controls available on API failure.
      if (error.name !== 'AbortError') section.dataset.reviewLoad = 'unavailable';
    }
  };
  document.querySelectorAll('[data-book-reviews]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-book-reviews]').forEach(initialize));
})();
